import { supabase } from '../lib/supabaseClient';
import { REGIONS } from '../data/geo';
import { listingProfileUrl } from '../utils/listingUrl';

/**
 * Atlas Destination, data layer (the Automatic/Hybrid sources behind a destination
 * page). Spec: _ATLAS_DESTINATION_ARCHITECTURE.md. Each fetcher returns only what
 * exists; the page hides empty sections (graceful degradation).
 */

const CARD = 'id, name, slug, country_slug, region_slug, category_slug, city, region, card_image, hero_image';
const POST = 'id, slug, title, cover_image, category_label, published_at, reading_time';
const VENUE_CATS = new Set(['wedding-venues', 'venues']);

export const slugify = (s) => (s ?? '').toString().toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
export const listingUrl = (l) => listingProfileUrl(l);
export const listingImage = (l) => l.card_image || l.hero_image || null;

/** The curated editorial guide for this destination (Editorial Identity, seasons, FAQs, …). */
export async function fetchRegionGuide(regionSlug) {
  const { data } = await supabase.from('region_guides').select('*').eq('region_slug', regionSlug).eq('published', true).limit(1);
  return data?.[0] || null;
}

// Builds the region_slug/city_slug OR clause for one or more guide-provided slugs.
// Accepts a bare string (the common case — the guide's own region_slug) or an
// array (region_guides.inventory_slugs, for guides with a genuinely multi-area
// scope or a compound-region mismatch a single slug rename can't fix). Every
// value is checked against BOTH listings.region_slug and listings.city_slug, so
// a city-level guide and a multi-area guide use the exact same matching logic.
export function buildInventoryOrClause(regionSlugOrSlugs) {
  const slugs = Array.isArray(regionSlugOrSlugs) ? regionSlugOrSlugs : [regionSlugOrSlugs];
  const clauses = slugs.flatMap((s) => [`region_slug.eq.${s}`, `city_slug.eq.${s}`]);
  return clauses.join(',');
}

/** Venues in this destination (Automatic). */
export async function fetchDestinationVenues(regionSlugOrSlugs, limit = 6) {
  // Some region_guides rows are city-level (e.g. "florence") while listings.region_slug
  // is the broader region ("tuscany") — those listings carry the city separately in
  // city_slug. A small number of guides (region_guides.inventory_slugs, set explicitly
  // per guide) have a genuinely multi-area scope or a compound-region mismatch that a
  // single slug can't express — those pass an array here instead of a bare string.
  // A listing can only be counted once per guide: region_slug and city_slug are always
  // different values for the same row, and inventory_slugs values were each verified
  // individually before being added, so the OR can't double-count a single listing.
  const { data } = await supabase.from('listings').select(CARD).eq('status', 'published').or(buildInventoryOrClause(regionSlugOrSlugs)).order('id', { ascending: true }).limit(40);
  return (data || []).filter((l) => VENUE_CATS.has(l.category_slug) || l.category_slug == null).slice(0, limit);
}

/** Wedding creatives (planners/photographers/florists/…) in this destination (Automatic). */
export async function fetchDestinationCreatives(regionSlugOrSlugs, limit = 8) {
  const { data } = await supabase.from('listings').select(CARD).eq('status', 'published').or(buildInventoryOrClause(regionSlugOrSlugs)).order('id', { ascending: true }).limit(40);
  return (data || []).filter((l) => l.category_slug && !VENUE_CATS.has(l.category_slug)).slice(0, limit);
}

/** Edits about this destination, by location_tags or by the entity graph (Automatic). */
export async function fetchDestinationEdits(regionSlug, regionName, limit = 6) {
  const want = new Set([regionSlug, slugify(regionName)].filter(Boolean));
  const { data } = await supabase
    .from('magazine_posts')
    .select(`${POST}, location_tags, mentions_json`)
    .eq('published', true)
    .order('published_at', { ascending: false })
    .order('id', { ascending: true })
    .limit(300);
  return (data || [])
    .filter((p) =>
      (p.location_tags || []).some((t) => want.has(slugify(t))) ||
      (p.mentions_json || []).some((m) => want.has(slugify(m?.name))))
    .slice(0, limit);
}

/** Nearby destinations (Automatic), explicit geo relations, then nearest in-country by lat/lng. */
export function nearbyDestinations(regionSlug, max = 4) {
  const r = REGIONS.find((x) => x && x.slug === regionSlug);
  if (!r) return [];
  const near = (r.relatedRegionSlugs || []).map((s) => REGIONS.find((x) => x && x.slug === s)).filter(Boolean);
  if (near.length < max && r.lat && r.lng) {
    const dist = (a, b) => Math.hypot(a.lat - b.lat, a.lng - b.lng);
    const cands = REGIONS
      .filter((x) => x && x.slug !== regionSlug && x.slug !== 'all' && x.countrySlug === r.countrySlug && x.lat && x.lng)
      .map((x) => ({ x, d: dist(r, x) }))
      .sort((a, b) => a.d - b.d)
      .map((o) => o.x);
    for (const c of cands) { if (near.length >= max) break; if (!near.find((n) => n.slug === c.slug)) near.push(c); }
  }
  return near.slice(0, max).map((x) => ({ name: x.name, slug: x.slug, url: `/${x.countrySlug}/${x.slug}`, heroImg: x.heroImg }));
}

/** One call for the whole page. */
export async function fetchDestination({ regionSlug, regionName }) {
  const [guide, venues, creatives, edits] = await Promise.all([
    fetchRegionGuide(regionSlug),
    fetchDestinationVenues(regionSlug),
    fetchDestinationCreatives(regionSlug),
    fetchDestinationEdits(regionSlug, regionName),
  ]);
  return { guide, venues, creatives, edits, nearby: nearbyDestinations(regionSlug) };
}
